import Image from 'next/image';
import { NAV_ITEMS } from '@/_shared/constants/nav-items.constant';
import { useRouter } from 'next/router';
import { useSidebar } from '@/_shared/context/SidebarContext';
import { useWindowSize } from '@uidotdev/usehooks';
import { useEffect, useState } from 'react';
import { NavItem } from '@/_shared/models/nav-item.model';
import {
  ChevronDown,
  ChevronUp,
  FileSearch,
  Flag,
  Home,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import React from 'react';
import { Button } from '../buttons';

const Sidebar = () => {
  const { collapsed, setCollapsed, touched, collapseSidebar, toggleCollapse } =
    useSidebar();

  const router = useRouter();
  const { pathname } = router;
  const { width } = useWindowSize();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const active = 'bg-gray-100 hover:bg-gray-200';
  const itemBaseClass =
    'flex h-10 items-center rounded-lg p-2 hover:bg-indigo-100';
  const itemLayoutClass = collapsed
    ? 'w-10 justify-center self-center'
    : 'w-full gap-2 pl-3';

  const handleNavigation = async (path?: string, newTab?: boolean) => {
    if (width && width <= 1280) {
      collapseSidebar();
    }

    if (!path || pathname === path) return;

    if (newTab) {
      window.open(path, '_blank');
      return;
    }

    try {
      const navigated = await router.push(path, undefined, { shallow: false });

      if (!navigated && typeof window !== 'undefined') {
        window.location.assign(path);
      }
    } catch {
      if (typeof window !== 'undefined') {
        window.location.assign(path);
      }
    }
  };

  useEffect(() => {
    if (touched || !width) return;
    if (!collapsed && width <= 1280) {
      setCollapsed(true);
    }
    if (collapsed && width > 1280) {
      setCollapsed(false);
    }
  }, [collapsed, setCollapsed, touched, width]);

  useEffect(() => {
    setOpenSections((prev) => {
      const next = { ...prev };
      let changed = false;

      NAV_ITEMS.forEach((button) => {
        if (!button.child || !button.id) return;

        const isActiveSection = Boolean(
          button.section && pathname.startsWith(button.section)
        );

        // Only auto-expand the section for the current route — never
        // force-collapse a section the user expanded manually just because
        // they navigated to a different module.
        if (isActiveSection && !prev[button.id]) {
          next[button.id] = true;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [pathname]);

  const toggleSection = (button: NavItem) => {
    if (!button.id) return;

    if (collapsed) {
      toggleCollapse();
      setOpenSections((prev) => ({ ...prev, [button.id]: true }));
      return;
    }

    setOpenSections((prev) => ({
      ...prev,
      [button.id]: !prev[button.id],
    }));
  };

  return (
    <div className="hidden lg:block">
      <div
        className={`flex h-full min-h-0 flex-col overflow-y-auto border-r border-r-gray-200 bg-white py-4 px-2 transition-all duration-300 ease-in-out z-50 ${
          collapsed ? 'w-14 xl:w-18' : 'lg:w-52 xl:w-64'
        }`}
      >
        <div
          className={`sticky top-0 z-10 mb-5 flex h-10 shrink-0 items-center bg-white xl:p-0 ${
            collapsed ? 'justify-center' : 'justify-between px-2'
          }`}
        >
          {!collapsed && (
            <Image
              className="h-full w-[calc(100%-24px)]"
              src="/assets/MeterCube_by_Cube.svg"
              width={1000}
              height={500}
              alt="MeterCube logo"
            />
          )}
          <button
            type="button"
            className={`text-center transform transition-transform duration-75 hover:scale-110 ${
              collapsed
                ? 'flex min-h-10 min-w-10 items-center justify-center p-2'
                : 'py-2 pl-2'
            }`}
            onClick={toggleCollapse}
          >
            {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
          </button>
        </div>

        <div
          className={`flex min-h-0 flex-1 flex-col gap-2 ${
            collapsed ? 'items-center' : ''
          }`}
        >
          <a
            href="/hub"
            onClick={(e) => {
              e.preventDefault();
              void handleNavigation('/hub');
            }}
            className={`${itemBaseClass} ${pathname === '/hub' && active} ${itemLayoutClass}`}
          >
            <span>
              <Home />
            </span>
            {!collapsed && <span className="text-sm">Hub</span>}
          </a>

          {NAV_ITEMS.map((button) => {
            const isSectionActive = Boolean(
              button.section && pathname.startsWith(button.section)
            );
            const isSectionOpen = Boolean(button.id && openSections[button.id]);

            return (
              <React.Fragment key={button.id}>
                {button.child ? (
                  <div
                    className={`${itemBaseClass} ${(pathname === button.route || isSectionActive) && active} ${itemLayoutClass}`}
                  >
                    <a
                      href={button.route}
                      onClick={(e) => {
                        e.preventDefault();
                        void handleNavigation(button.route);
                      }}
                      className={`flex items-center ${collapsed ? 'justify-center' : 'w-full gap-2'}`}
                    >
                      <span>{button.icon}</span>
                      {!collapsed && (
                        <span className="whitespace-nowrap text-sm">
                          {button.label}
                        </span>
                      )}
                    </a>
                    {!collapsed && (
                      <button
                        type="button"
                        onClick={() => toggleSection(button)}
                        className="rounded p-0.5 transition-colors hover:bg-indigo-200"
                        aria-label={`Toggle ${button.label} submenu`}
                      >
                        {isSectionOpen ? <ChevronUp /> : <ChevronDown />}
                      </button>
                    )}
                  </div>
                ) : (
                  <a
                    href={button.route}
                    onClick={(e) => {
                      e.preventDefault();
                      void handleNavigation(button.route);
                    }}
                    className={`${itemBaseClass} ${pathname === button.route && active} ${itemLayoutClass}`}
                  >
                    <span>{button.icon}</span>
                    {!collapsed && (
                      <span className="whitespace-nowrap text-sm">
                        {button.label}
                      </span>
                    )}
                  </a>
                )}

                {!collapsed && button.child && isSectionOpen && (
                  <div className="ml-4 flex flex-col gap-y-2 border-l border-l-gray-300 pl-3">
                    {button.child.map((sub) => (
                      <button
                        key={sub.id}
                        type="button"
                        onClick={() => void handleNavigation(sub.route)}
                        className={`h-10 items-center rounded-lg p-2 hover:bg-indigo-100 gap-2 ${
                          pathname === sub.route && active
                        } flex w-full`}
                      >
                        <span className="whitespace-nowrap text-sm">
                          {sub.label}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
          <div className="mt-auto flex flex-col gap-2 pt-6">
            {/* <button
              type="button"
              onClick={() => void handleNavigation('/support')}
              className={`flex h-11 items-center rounded-xl bg-indigo-50 text-indigo-700 transition-colors hover:bg-indigo-100 ${
                collapsed
                  ? 'w-10 justify-center self-center'
                  : 'w-full gap-2 px-3'
              }`}
            >
              <Headphones className="size-4 shrink-0" />
            </button> */}
            <Button
              variant="filter"
              type="button"
              onClick={() => void handleNavigation('/report-issue', true)}
              className={`flex h-11 items-center ${
                collapsed
                  ? 'w-10 justify-center self-center'
                  : 'w-full gap-2 px-3'
              }`}
            >
              {!collapsed ? (
                <div className="flex gap-2 items-center">
                  <Flag className="size-4 shrink-0" />
                  <span className="text-sm font-medium">Report Issue</span>
                </div>
              ) : (
                <Flag className="size-4 shrink-0" />
              )}
            </Button>
            <Button
              variant="outline-gray"
              type="button"
              onClick={() => handleNavigation('/issue-tracker')}
              className={`flex h-11 items-center ${
                collapsed
                  ? 'w-10 justify-center self-center'
                  : 'w-full justify-center gap-2 px-3'
              }`}
            >
              {!collapsed ? (
                <div className="flex gap-2 items-center">
                  <FileSearch className="size-4 shrink-0" />
                  <span className="text-sm font-medium">Issue Tracker</span>
                </div>
              ) : (
                <FileSearch className="size-4 shrink-0" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
